import { ActionsDropdown } from '@app/components/actions-dropdown/ActionsDropdown';
import { StyledButtonGroup } from '@app/components/button-group/ButtonGroup';
import { TextField } from '@app/components/form-fields/TextField';
import useBrands from '@app/hooks/brands/useBrands';
import useChannelPartners from '@app/hooks/channelPartners/useChannelPartners';
import type { LocationListParams } from '@app/services/location.types';
import type { MenuProps } from 'antd';
import { debounce } from 'lodash';
import { useEffect, useState } from 'react';
import { MdSearch } from 'react-icons/md';

export type LocationFiltersProps = {
    onChange: (values: LocationListParams) => void;
};

export const LocationFilters = ({ onChange }: LocationFiltersProps) => {
    const { data: channelPartnersData } = useChannelPartners({
        per_page: 99999,
    });
    const { data: brandList } = useBrands({ per_page: 99999 });

    const [filterSelection, setFilterSelection] = useState<LocationListParams>({
        search: '',
        brand_id: [],
        channel_id: [],
    });

    useEffect(() => {
        onChange(filterSelection);
    }, [filterSelection]);

    const updateFilterSelection = (type: keyof typeof filterSelection, value: string[]) => {
        if (value.includes('reset')) {
            setFilterSelection((prev) => ({
                ...prev,
                [type]: [],
            }));
            return;
        }
        setFilterSelection((prev) => ({
            ...prev,
            [type]: value,
        }));
    };

    const generateFilterOptions = (options: MenuProps['items'] = []): MenuProps['items'] => {
        if (!options) {
            return [];
        }

        return [
            ...options,
            { type: 'divider' },
            {
                key: 'reset',
                label: <span className="text-red">Clear</span>,
            },
        ];
    };

    const channelPartnerOptions =
        channelPartnersData?.data?.map((item) => ({
            key: item.id,
            label: item.name,
        })) || [];

    const brandOptions =
        brandList?.data?.map((item) => ({
            key: item.id,
            label: item.name,
        })) || [];

    const filters = [
        {
            id: 'brand_id',
            label: 'Brand',
            multiple: true,
            items: generateFilterOptions(brandOptions),
        },
        {
            id: 'channel_id',
            label: 'Channel Partner',
            multiple: true,
            items: generateFilterOptions(channelPartnerOptions),
        },
    ];

    return (
        <div>
            <TextField
                placeholder="Search Locations..."
                prefix={<MdSearch size={20} />}
                onChange={debounce((e) => {
                    setFilterSelection((prev) => ({
                        ...prev,
                        search: e.target.value,
                    }));
                }, 500)}
            />
            <div className="styled-table-filters">
                <StyledButtonGroup justify="flex-start">
                    {filters.map((filter) => {
                        const itemId = filter.id as keyof LocationListParams;
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        const value = filterSelection?.[itemId] as any;
                        return (
                            <ActionsDropdown
                                key={filter.id}
                                title={
                                    <>
                                        {filter.label}
                                        {value?.length ? <strong> ({value?.length})</strong> : null}
                                    </>
                                }
                                items={filter.items || []}
                                menu={{
                                    selectable: true,
                                    selectedKeys: value,
                                    multiple: filter.multiple,
                                    onSelect: ({ selectedKeys }) =>
                                        updateFilterSelection(itemId, selectedKeys),
                                    onDeselect: ({ selectedKeys }) =>
                                        updateFilterSelection(itemId, selectedKeys),
                                }}
                                buttonProps={{
                                    size: 'small',
                                    shape: 'round',
                                    type: 'default',
                                    color: value?.length ? 'green' : 'default',
                                }}
                            />
                        );
                    })}
                </StyledButtonGroup>
            </div>
        </div>
    );
};

export default LocationFilters;
